import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { brandsAPI } from '../../api/brandsAPI';

const OTHER_OPTION = { id: '__other__', name: 'Other', slug: 'other', is_verified: false };

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SearchWrap = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 0.5rem;
  border: 1.5px solid ${(p) => (p.$error ? '#ef4444' : '#d1d5db')};
  font-size: 0.95rem;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 129, 129, 0.15);
  }

  &::placeholder {
    color: #6b7280;
  }
`;

const Dropdown = styled.ul`
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  margin: 0;
  padding: 0.35rem 0;
  list-style: none;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
`;

const DropdownItem = styled.li`
  padding: 0.65rem 0.9rem;
  cursor: pointer;
  font-size: 0.92rem;
  color: #1f2937;

  &:hover,
  &[data-active='true'] {
    background: #ecfdf5;
  }
`;

const SelectedChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: #ecfdf5;
  color: #065f46;
  font-size: 0.88rem;
  font-weight: 600;
`;

const ClearBtn = styled.button`
  border: none;
  background: transparent;
  color: #047857;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
`;

const ErrorText = styled.span`
  color: #ef4444;
  font-size: 0.82rem;
`;

const LoadingText = styled.span`
  color: #6b7280;
  font-size: 0.82rem;
`;

const Hint = styled.span`
  display: block;
  font-size: 0.8rem;
  color: #6b7280;
`;

const CustomNameInput = styled.input`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 0.5rem;
  border: 1.5px solid ${(p) => (p.$error ? '#ef4444' : '#d1d5db')};
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 129, 129, 0.15);
  }
`;

const EMPTY_VALUE = {
  brandId: null,
  brandName: null,
  isNoBrand: false,
  isLocalBrand: false,
  customBrandName: '',
};

const filterCatalogBrands = (brands) =>
  (brands || []).filter((brand) => brand?.slug !== 'local-brand');

const BrandSelector = ({
  value = EMPTY_VALUE,
  onChange,
  mode = 'listing',
  filterValue = '',
  onFilterChange,
  required = true,
  error = '',
  disabled = false,
}) => {
  const isFilterMode = mode === 'filter';
  const selection = value || EMPTY_VALUE;
  const [isOther, setIsOther] = useState(() => (
    isFilterMode ? false : Boolean(selection.isLocalBrand)
  ));
  const [query, setQuery] = useState(() => {
    if (isFilterMode) return '';
    return selection.isLocalBrand ? '' : (selection.brandName || '');
  });
  const [customName, setCustomName] = useState(() => {
    if (isFilterMode) return filterValue || '';
    return selection.customBrandName || '';
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  const selectedFilterName = isFilterMode ? String(filterValue || '').trim() : '';
  const showFilterChip = isFilterMode && Boolean(selectedFilterName) && !isOther;
  const showListingChip = !isFilterMode && Boolean(selection.brandId);
  const showListingOther = !isFilterMode && Boolean(selection.isLocalBrand);

  useEffect(() => {
    if (isFilterMode) {
      if (!selectedFilterName) {
        setIsOther(false);
        setCustomName('');
      }
      return;
    }
    if (selection.isLocalBrand) {
      setIsOther(true);
      setCustomName(selection.customBrandName || '');
      setQuery('');
    } else if (selection.brandId) {
      setIsOther(false);
      setQuery(selection.brandName || '');
      setCustomName('');
    }
  }, [
    isFilterMode,
    selectedFilterName,
    selection.brandId,
    selection.brandName,
    selection.isLocalBrand,
    selection.customBrandName,
  ]);

  const runSearch = useCallback(async (term) => {
    setLoading(true);
    try {
      const brands = await brandsAPI.searchBrands(term, 200);
      setResults(filterCatalogBrands(brands));
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOther || showFilterChip || showListingChip) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(query.trim());
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [isOther, query, runSearch, showFilterChip, showListingChip]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectBrand = (brand) => {
    if (isFilterMode) {
      onFilterChange?.(brand.name);
      setIsOther(false);
      setCustomName('');
      setQuery('');
      setOpen(false);
      setFocused(false);
      return;
    }
    onChange?.({
      brandId: brand.id,
      brandName: brand.name,
      isNoBrand: false,
      isLocalBrand: false,
      customBrandName: '',
    });
    setQuery(brand.name);
    setCustomName('');
    setIsOther(false);
    setOpen(false);
    setFocused(false);
  };

  const selectOther = () => {
    setIsOther(true);
    setQuery('');
    setOpen(false);
    setFocused(false);
    if (isFilterMode) {
      onFilterChange?.(customName.trim());
      return;
    }
    onChange?.({
      brandId: null,
      brandName: 'Other',
      isNoBrand: false,
      isLocalBrand: true,
      customBrandName: customName,
    });
  };

  const updateCustomName = (name) => {
    setCustomName(name);
    if (isFilterMode) {
      onFilterChange?.(name.trim());
      return;
    }
    onChange?.({
      brandId: null,
      brandName: 'Other',
      isNoBrand: false,
      isLocalBrand: true,
      customBrandName: name,
    });
  };

  const clearSelection = () => {
    if (isFilterMode) {
      onFilterChange?.('');
    } else {
      onChange?.(EMPTY_VALUE);
    }
    setQuery('');
    setCustomName('');
    setResults([]);
    setIsOther(false);
    setOpen(false);
    setFocused(false);
  };

  const effectiveRequired = isFilterMode ? false : required;
  const placeholder = focused || query
    ? 'Type to search brands…'
    : (isFilterMode ? 'Search brands…' : (effectiveRequired ? 'Brand Name *' : 'Brand Name'));

  const dropdownItems = [...results, OTHER_OPTION];
  const showSearch = !showFilterChip && !showListingChip && !showListingOther && !isOther;

  return (
    <Wrap>
      <SearchWrap ref={wrapRef}>
        {showFilterChip || showListingChip ? (
          <SelectedChip>
            {isFilterMode ? selectedFilterName : selection.brandName}
            <ClearBtn type="button" onClick={clearSelection} disabled={disabled}>Change</ClearBtn>
          </SelectedChip>
        ) : showListingOther || isOther ? (
          <SelectedChip>
            Other
            <ClearBtn type="button" onClick={clearSelection} disabled={disabled}>Change</ClearBtn>
          </SelectedChip>
        ) : null}

        {showSearch ? (
          <>
            <SearchInput
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => {
                setFocused(true);
                setOpen(true);
                if (!results.length) runSearch(query.trim());
              }}
              placeholder={placeholder}
              disabled={disabled}
              $error={Boolean(error)}
              autoComplete="off"
              aria-label={isFilterMode ? 'Filter by brand' : 'Brand Name'}
            />
            {loading && <LoadingText>Searching…</LoadingText>}
            {open && (
              <Dropdown>
                {dropdownItems.map((brand) => (
                  <DropdownItem
                    key={brand.id}
                    data-active={!isFilterMode && selection.brandId === brand.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (brand.id === OTHER_OPTION.id) selectOther();
                      else selectBrand(brand);
                    }}
                  >
                    {brand.name}
                    {brand.is_verified ? ' ✓' : ''}
                  </DropdownItem>
                ))}
                {!loading && results.length === 0 && query.trim() ? (
                  <DropdownItem as="li" style={{ color: '#6b7280', cursor: 'default' }}>
                    No matching brands — choose Other
                  </DropdownItem>
                ) : null}
              </Dropdown>
            )}
          </>
        ) : null}
      </SearchWrap>

      {isOther || showListingOther ? (
        <CustomNameInput
          type="text"
          value={customName}
          onChange={(e) => updateCustomName(e.target.value)}
          placeholder={isFilterMode ? 'Enter brand name to filter…' : 'Enter brand name…'}
          disabled={disabled}
          $error={Boolean(error && !customName.trim())}
          autoComplete="off"
          autoFocus
        />
      ) : null}

      {error ? <ErrorText>{error}</ErrorText> : null}
      {!isFilterMode && !effectiveRequired && !error ? (
        <Hint>Optional — helps buyers find your item faster.</Hint>
      ) : null}
    </Wrap>
  );
};

export default BrandSelector;
